import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faCreditCard,
  faUsers,
  faMoneyCheckAlt,
  faCogs,
  faChartLine,
  faUserGraduate,
  faHome,
  faHistory,
  faShieldAlt,
  faRobot,
  faReceipt,
  faSearch,
  faFilter,
  faThLarge,
  faList,
  faSyncAlt,
  faArrowRight,
  faLockOpen,
  faChartBar,
  faClock,
  faUserCheck,
  faPercent,
  faCheckCircle,
  faExclamationTriangle,
  faGraduationCap,
  faUniversity,
  faRupeeSign,
  faFileInvoiceDollar,
  faHandHoldingUsd,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Sidebar from "./Sidebar";
import "./Finance.css";

const Finance = () => {
  const navigate = useNavigate();

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [activeAccounts, setActiveAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // NEW: State for Show/Hide controls (Default: HIDDEN)
  const [showDetails, setShowDetails] = useState(false);

  const menuCards = [
    {
      title: "ADD SOLO FEE",
      subtitle: "Apply Fee and Expenses to Individual Students",
      icon: faReceipt,
      color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      onClick: () => navigate("/Fee-ApplyTo-Solo-Student"),
      badge: "New",
    },
    {
      title: "SOLO FEE PAYMENT",
      subtitle: "Process Individual Student Payments",
      icon: faCreditCard,
      color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      onClick: () => navigate("/Fee-PaymentOf-Solo-Student"),
      stats: "Quick Access",
    },
    {
      title: "ADD FAMILY FEE",
      subtitle: "Apply Fees to Family Accounts",
      icon: faUsers,
      color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      onClick: () => navigate("/Fee-ApplyTo-Family-Student"),
      featured: false,
    },
    {
      title: "FAMILY FEE PAYMENT",
      subtitle: "Process Family Account Payments",
      icon: faMoneyCheckAlt,
      color: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      onClick: () => navigate("/Fee-PaymentOf-Family-Student"),
      stats: "Bulk Processing",
    },
    {
      title: "ACCOUNTS SETUP",
      subtitle: "Configure Fee Structures and Expenses",
      icon: faCogs,
      color: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
      onClick: () => navigate("/Accounts-Setup"),
      badge: "Admin",
    },
    // {
    //   title: "FEE ANALYTICS",
    //   subtitle: "View Financial Reports and Insights",
    //   icon: faChartLine,
    //   color: "linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)",
    //   onClick: () => navigate("/Fee-Analytics"),
    //   new: true,
    // },
    // {
    //   title: "STUDENT ACCOUNTS",
    //   subtitle: "Manage Individual Student Financial Records",
    //   icon: faUserGraduate,
    //   color: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    //   onClick: () => navigate("/Student-Accounts"),
    //   stats: "300+ Active",
    // },
    // {
    //   title: "FAMILY PORTALS",
    //   subtitle: "Family Account Management Dashboard",
    //   icon: faHome,
    //   color: "linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)",
    //   onClick: () => navigate("/Family-Portals"),
    //   featured: false,
    // },
    {
      title: "Fee Defaulters",
      subtitle: "Complete Transaction Records and Logs",
      icon: faHistory,
      color: "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)",
      onClick: () => navigate("/Fee Defaulters"),
      stats: "All Records",
    },
    // {
    //   title: "SECURITY SETTINGS",
    //   subtitle: "Configure Payment Security & Permissions",
    //   icon: faShieldAlt,
    //   color: "linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)",
    //   onClick: () => navigate("/Security-Settings"),
    //   badge: "Secure",
    // },
    {
      title: "AUTO-INVOICING (UPCOMING)",
      subtitle: "Automated Billing and Invoice Generation",
      icon: faRobot,
      color: "linear-gradient(135deg, #ffd89b 0%, #19547b 100%)",
      onClick: () => navigate(""),
      new: true,
    },
    // {
    //   title: "SYSTEM SETTINGS",
    //   subtitle: "Advanced Configuration and Preferences",
    //   icon: faCogs,
    //   color: "linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)",
    //   onClick: () => navigate("/System-Settings"),
    //   badge: "Advanced",
    // },
  ];

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch dashboard stats
      const statsResponse = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageAccDashboard.php?action=get-finance-dashboard-stats`
      );
      const statsData = await statsResponse.json();

      if (statsData.success) {
        setDashboardData(statsData.data);
      } else {
        throw new Error(statsData.error || "Failed to fetch dashboard stats");
      }

      // Fetch recent payments
      const paymentsResponse = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageAccDashboard.php?action=get-recent-payments&limit=5`
      );
      const paymentsData = await paymentsResponse.json();

      if (paymentsData.success) {
        setRecentPayments(paymentsData.data);
      }

      // Fetch active accounts
      const accountsResponse = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageAccDashboard.php?action=get-active-accounts`
      );
      const accountsData = await accountsResponse.json();

      if (accountsData.success) {
        setActiveAccounts(accountsData.data);
      }
    } catch (err) {
      setError("Failed to load dashboard data: " + err.message);
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search financial records
  const handleSearch = async (e) => {
    e.preventDefault();

    if (searchQuery.length < 2) {
      alert("Please enter at least 2 characters to search");
      return;
    }

    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://mhsdev.online/MHS-WEB/api/ManageAccDashboard.php?action=search-financial-records&query=${encodeURIComponent(
          searchQuery
        )}&limit=10`
      );
      const data = await response.json();

      if (data.success) {
        setSearchResults(data);
      } else {
        throw new Error(data.error || "Search failed");
      }
    } catch (err) {
      setError("Search failed: " + err.message);
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchDashboardData();
  };

  useEffect(() => {
    fetchDashboardData();

    // Refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 300000);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString("en-PK") || "0";
  };

  // Get icon for activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case "success":
        return faCheckCircle;
      case "info":
        return faUserPlus;
      case "warning":
        return faExclamationTriangle;
      default:
        return faClock;
    }
  };

  // Get icon color for activity type
  const getActivityIconColor = (type) => {
    switch (type) {
      case "success":
        return "#43e97b";
      case "info":
        return "#4facfe";
      case "warning":
        return "#fa709a";
      default:
        return "#a18cd1";
    }
  };

  // Get activity description
  const getActivityDescription = (activity) => {
    if (activity.type === "payment") {
      return `Payment received from ${activity.name}`;
    }
    return activity.description || "Financial activity";
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
  };

  return (
    <div className="finance-dashboard-wrapper">
      <Sidebar />

      <main className="finance-main-content">
        {/* Professional Header with PKR Context */}
        <header className="finance-professional-header">
          <div className="finance-header-content">
            <div className="finance-header-text">
              <h1 className="finance-header-title">
                <FontAwesomeIcon
                  icon={faUniversity}
                  className="finance-header-title-icon"
                />
                <span style={{color:'white'}}>Accounts Management</span>
              </h1>
              <p className="finance-header-subtitle">
                Comprehensive Financial Management System for Educational
                Institutions in Pakistan
              </p>
            </div>

            {dashboardData && (
              <div className="finance-header-stats">
                <div className="finance-stat-item">
                  <span className="finance-stat-value">
                    <FontAwesomeIcon
                      icon={faRupeeSign}
                      style={{ color: "green" }}
                    />{" "}
                    {dashboardData.total_revenue_formatted || "PKR 0"}
                  </span>
                  <span className="finance-stat-label">
                    Total Revenue (PKR)
                  </span>
                </div>
                <div className="finance-stat-item">
                  <span className="finance-stat-value">
                    {dashboardData.collection_rate_formatted || "0%"}
                  </span>
                  <span className="finance-stat-label">Collection Rate</span>
                </div>
                <div className="finance-stat-item">
                  <span className="finance-stat-value">
                    {dashboardData.active_accounts_formatted || "0"}
                  </span>
                  <span className="finance-stat-label">Active Accounts</span>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* ----------------------------------------------- */}
        {/* NEW: Show/Hide Toggle for Details & Filters      */}
        {/* ----------------------------------------------- */}
        <div className="finance-toggle-container" onClick={() => setShowDetails(!showDetails)}>
          <span className="finance-toggle-text">
            {showDetails ? "Hide Details & Filters" : "Show Details & Filters"}
          </span>
          <div className={`finance-toggle-switch ${showDetails ? "active" : ""}`}>
            <div className="finance-toggle-slider"></div>
          </div>
        </div>

        {/* ----------------------------------------------- */}
        {/* HIDDEN BY DEFAULT: Search & Filter Bar           */}
        {/* ----------------------------------------------- */}
        {showDetails && (
          <div className="finance-search-filter-bar">
            <div className="finance-search-container">
              <FontAwesomeIcon icon={faSearch} className="finance-search-icon" />
              <input
                type="text"
                placeholder="Search student ID, family code, or name..."
                className="finance-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch(e)}
              />
              {searchQuery && (
                <button
                  className="finance-clear-search-btn"
                  onClick={handleClearSearch}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
              <button
                className="finance-search-btn"
                onClick={handleSearch}
                disabled={searchLoading}
              >
                {searchLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  "Search"
                )}
              </button>
            </div>
            <div className="finance-filter-actions">
              <button className="finance-filter-btn">
                <FontAwesomeIcon icon={faFilter} />
                Filters
              </button>
              <button className="finance-view-btn finance-active">
                <FontAwesomeIcon icon={faThLarge} />
              </button>
              <button className="finance-view-btn">
                <FontAwesomeIcon icon={faList} />
              </button>
              <button
                className="finance-refresh-btn"
                onClick={handleRefresh}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
              </button>
            </div>
          </div>
        )}

        {/* Search Results (Always visible if results exist) */}
        {searchResults && (
          <div className="finance-search-results-container">
            <div className="finance-search-results-header">
              <h3>Search Results ({searchResults.total_results || 0})</h3>
              <button
                className="finance-close-results-btn"
                onClick={handleClearSearch}
              >
                ✕
              </button>
            </div>
            {searchResults.results && searchResults.results.length > 0 ? (
              <div className="finance-search-results-grid">
                {searchResults.results.map((result, index) => (
                  <div key={index} className="finance-search-result-card">
                    <div className="finance-result-header">
                      <h4>{result.account_name}</h4>
                      <span
                        className={`finance-result-type finance-${result.account_type}`}
                      >
                        {result.account_type.toUpperCase()}
                      </span>
                    </div>
                    <div className="finance-result-details">
                      <p>ID: {result.account_id}</p>
                      <p>Total Paid: PKR {formatNumber(result.total_paid)}</p>
                      <p>Pending: PKR {formatNumber(result.total_pending)}</p>
                      <p>Records: {result.records.length}</p>
                    </div>
                    <button
                      className="finance-view-details-btn"
                      onClick={() =>
                        navigate(`/student-accounts/${result.account_id}`)
                      }
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="finance-no-results">
                <p>No results found for "{searchQuery}"</p>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && !searchResults && (
          <div className="finance-dashboard-loading">
            <div className="finance-loading-spinner">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
            <p>Loading dashboard data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="finance-dashboard-error">
            <div className="finance-error-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <p>{error}</p>
            <button className="finance-retry-btn" onClick={fetchDashboardData}>
              Retry
            </button>
          </div>
        )}

        {/* Main Cards Grid - Only show if not loading and no error */}
        {!loading && !error && !searchResults && (
          <>
            {/* ----------------------------------------------- */}
            {/* HIDDEN BY DEFAULT: Quick Stats Dashboard          */}
            {/* ----------------------------------------------- */}
            {showDetails && dashboardData && (
              <div className="finance-quick-stats-dashboard">
                <div className="finance-stats-card finance-total-revenue">
                  <div className="finance-stats-icon">
                    <FontAwesomeIcon icon={faRupeeSign} />
                  </div>
                  <div className="finance-stats-content">
                    <h4>Total Revenue</h4>
                    <h2>{dashboardData.total_revenue_formatted || "PKR 0"}</h2>
                    <p className="finance-stats-change finance-positive">
                      ↑ {dashboardData.monthly_growth_formatted || "0%"} this
                      month
                    </p>
                  </div>
                </div>

                <div className="finance-stats-card finance-pending-payments">
                  <div className="finance-stats-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div className="finance-stats-content">
                    <h4>Pending Payments</h4>
                    <h2>
                      {dashboardData.pending_payments_formatted || "PKR 0"}
                    </h2>
                    <p className="finance-stats-change finance-warning">
                      {dashboardData.overdue_accounts || 0} overdue accounts
                    </p>
                  </div>
                </div>

                <div className="finance-stats-card finance-active-accounts">
                  <div className="finance-stats-icon">
                    <FontAwesomeIcon icon={faUserCheck} />
                  </div>
                  <div className="finance-stats-content">
                    <h4>Active Accounts</h4>
                    <h2>{dashboardData.active_accounts_formatted || "0"}</h2>
                    <p className="finance-stats-change finance-positive">
                      ↑{" "}
                      {dashboardData.active_accounts
                        ? Math.floor(dashboardData.active_accounts * 0.1)
                        : 0}{" "}
                      new this week
                    </p>
                  </div>
                </div>

                <div className="finance-stats-card finance-collection-rate">
                  <div className="finance-stats-icon">
                    <FontAwesomeIcon icon={faPercent} />
                  </div>
                  <div className="finance-stats-content">
                    <h4>Collection Rate</h4>
                    <h2>{dashboardData.collection_rate_formatted || "0%"}</h2>
                    <p className="finance-stats-change finance-positive">
                      ↑ 2.3% from last month
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Cards Grid (Always visible) */}
            <section className="finance-content">
              <div className="finance-cards-grid">
                {menuCards.map((card, index) => (
                  <div
                    key={index}
                    className={`finance-professional-card ${
                      card.featured ? "finance-featured" : ""
                    }`}
                    onClick={card.onClick}
                    style={{ "--card-gradient": card.color }}
                  >
                    {/* Card Badges */}
                    {card.badge && (
                      <span className="finance-card-badge finance-badge-premium">
                        {card.badge}
                      </span>
                    )}
                    {card.new && (
                      <span className="finance-card-badge finance-badge-new">
                        NEW
                      </span>
                    )}

                    {/* Card Icon */}
                    <div className="finance-card-icon-wrapper">
                      <div
                        className="finance-card-icon-bg"
                        style={{ background: card.color }}
                      >
                        <FontAwesomeIcon
                          icon={card.icon}
                          className="finance-card-icon"
                        />
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="finance-card-content">
                      <h3 className="finance-card-title">{card.title}</h3>
                      <p className="finance-card-subtitle">{card.subtitle}</p>

                      {card.stats && (
                        <div className="finance-card-stats">
                          <span className="finance-stats-tag">
                            {card.stats}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div className="finance-card-footer">
                      <span className="finance-access-badge">
                        <FontAwesomeIcon icon={faLockOpen} />
                        Click to Access
                      </span>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="finance-arrow-icon"
                      />
                    </div>

                    {/* Hover Effect Overlay */}
                    <div className="finance-card-hover-effect"></div>
                  </div>
                ))}
              </div>
            </section>

            {/* Recent Activity (Always visible) */}
            <div className="finance-recent-activity">
              <div className="finance-activity-header">
                <h3>Recent Financial Activity</h3>
                <button
                  className="finance-view-all-btn"
                  onClick={() => navigate("/Payment-History")}
                >
                  View All →
                </button>
              </div>
              <div className="finance-activity-list">
                {recentPayments.length > 0 ? (
                  recentPayments.map((activity, index) => (
                    <div key={index} className="finance-activity-item">
                      <div
                        className="finance-activity-icon"
                        style={{
                          color: getActivityIconColor(activity.icon_type),
                        }}
                      >
                        <FontAwesomeIcon
                          icon={getActivityIcon(activity.icon_type)}
                        />
                      </div>
                      <div className="finance-activity-content">
                        <p>{getActivityDescription(activity)}</p>
                        <span className="finance-activity-time">
                          {activity.time_ago} • {activity.amount_formatted}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="finance-no-activity">
                    <p>No recent activity found</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Professional Footer */}
        <footer className="finance-professional-footer">
          <div className="finance-footer-content">
            <div className="finance-footer-left">
              <div className="finance-footer-logo">
                <FontAwesomeIcon icon={faGraduationCap} />
                <span>EduFinance Pro - Pakistan</span>
              </div>
              <p className="finance-footer-text">
                Advanced Financial Management System for Pakistani Educational
                Institutions
              </p>
            </div>
            <div className="finance-footer-right">
              <div className="finance-footer-links">
                <a href="/privacy">Privacy Policy</a>
                <a href="/terms">Terms of Service</a>
                <a href="/support">Support Center</a>
              </div>
              <p className="finance-footer-copyright">
                © 2024 My School Pakistan. All Rights Reserved.
                <span className="finance-version">v4.2.1</span>
              </p>
            </div>
          </div>
          <div className="finance-footer-bottom">
            <div className="finance-system-status">
              <span className="finance-status-dot finance-online"></span>
              System Status: <strong>All Systems Operational</strong>
            </div>
            <div className="finance-last-updated">
              Last updated:{" "}
              {new Date().toLocaleString("en-PK", {
                timeZone: "Asia/Karachi",
                hour12: true,
              })}
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Finance;